import React, { useState, useEffect } from "react";
import {
  IconButton,
  Typography,
  Box,
  Button,
  Stack,
  Select,
  MenuItem,
  SelectChangeEvent,
  Divider,
  Modal,
  Skeleton,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { brand, gray } from "../../../theme/themePrimitives";
import axios from "axios";
import { useToast } from "../../../context/ToastContext";
import { usePortal } from "../../../context/PortalsContext";
import {
  createOrUpdateFileHub,
  getFileHubById,
  getS3PresignedUrl,
} from "../../../services/api/apiClient";
import { PresignFileType } from "../../../services/api/types/portalConfiguration/uploadPhoto";
import {
  FileHubByIdResult,
  FileHubFileEnum,
} from "../../../services/api/types/fileHub/fileHub";
import { formatBytes } from "../../../utils/helpers";
import { EllipsisTooltip } from "../../../components/EllipsisTooltip/EllipsisTooltip";

interface AddNewFileModalProps {
  open: boolean;
  isEditMode: boolean;
  fileHubId: string | null;
  onClose: () => void;
  onSuccess?: () => void;
}

const MAX_FILE_BYTES = 2 * 1024 * 1024 * 1024; // 2GB

export const AddNewFileModal: React.FC<AddNewFileModalProps> = ({
  open,
  isEditMode,
  fileHubId,
  onClose,
  onSuccess,
}) => {
  const { showToast } = useToast();
  const [selectedFileType, setSelectedFileType] = useState<
    FileHubFileEnum | ""
  >("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fileError, setFileError] = useState<string>("");
  const [typeError, setTypeError] = useState<string>("");
  const [editingFileInfo, setEditingFileInfo] =
    useState<FileHubByIdResult | null>(null);
  const [loadingEditData, setLoadingEditData] = useState(false);

  const { portals } = usePortal();
  const selectedPortal = portals.selectedPortal?.portalUid || "";

  const originalType = editingFileInfo?.type ?? "";
  const typeChanged =
    isEditMode &&
    !!editingFileInfo &&
    selectedFileType !== "" &&
    selectedFileType !== originalType;

  const fileChanged = !!selectedFile;

  // Edit mode: enabled if typeChanged OR fileChanged
  // Create mode: enabled only if both file + type exist
  const canSubmit =
    !isSubmitting &&
    !loadingEditData &&
    !fileError &&
    (isEditMode
      ? typeChanged || fileChanged
      : !!selectedFileType && !!selectedFile);

  useEffect(() => {
    if (!open) return;

    // reset "new file" + errors each time you open
    setSelectedFile(null);
    setFileError("");
    setTypeError("");

    if (isEditMode) {
      setEditingFileInfo(null);
      fetchFileById();
    } else {
      setEditingFileInfo(null);
      setSelectedFileType("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isEditMode, fileHubId, selectedPortal]);

  const fetchFileById = async () => {
    if (!selectedPortal || !fileHubId) return;

    try {
      setLoadingEditData(true);
      const res = await getFileHubById(selectedPortal, fileHubId);

      setEditingFileInfo(res);
      setSelectedFileType(res.type);
    } catch (error: any) {
      console.log("error", error);
      showToast(error.response.data, "warning");
    } finally {
      setLoadingEditData(false);
    }
  };

  const formatFileName = (
    originalName: string,
    timestamp = new Date().toISOString().replace(/[-:]/g, "").split(".")[0] +
      "Z",
  ) => {
    const lastDot = originalName.lastIndexOf(".");

    if (lastDot <= 0) {
      return `${originalName}_${timestamp}`;
    }

    const base = originalName.slice(0, lastDot);
    const ext = originalName.slice(lastDot + 1);

    return `${base}_${timestamp}.${ext}`;
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const fileName = file.name.toLowerCase();
      if (fileName.endsWith(".exe")) {
        setSelectedFile(null);
        setFileError("Unsupported file type .exe files are not allowed.");
        event.target.value = "";
        return;
      }
      if (file.size > MAX_FILE_BYTES) {
        setSelectedFile(null);
        setFileError("File size exceeds 2GB limit.");
        event.target.value = "";
        return;
      }
      setFileError("");
      setSelectedFile(file);
    }
  };

  const handleExchange = async () => {
    if (!selectedPortal) return;

    // Create new file mode (must have both type and file uploaded)
    if (!isEditMode) {
      if (!selectedFileType || !selectedFile) return;
    }

    // Edit mode (must have backend info + at least one change)
    if (isEditMode) {
      if (!editingFileInfo) return;
      if (!typeChanged && !selectedFile) return;
    }

    try {
      setIsSubmitting(true);
      setTypeError("");

      // start with existing backend values
      let url = isEditMode ? editingFileInfo!.url : undefined;
      let size = isEditMode ? editingFileInfo!.size : undefined;

      // if user selected a new file, upload it and override url/size
      if (selectedFile) {
        const presigned = await getS3PresignedUrl(selectedPortal, {
          fileName: formatFileName(selectedFile.name),
          contentType: selectedFile.type,
          fileType: PresignFileType.FILE_HUB,
        });

        await axios.put(presigned.uploadUrl, selectedFile, {
          headers: { "Content-Type": selectedFile.type },
        });

        url = presigned.path;
        size = String(selectedFile.size);
      }

      // decide final type:
      // - create: selectedFileType exists
      // - edit: if user didn't change type, keep original
      const finalType = (
        isEditMode
          ? selectedFileType || editingFileInfo!.type
          : selectedFileType
      ) as FileHubFileEnum;

      await createOrUpdateFileHub(selectedPortal, {
        FileHubUid: isEditMode ? editingFileInfo!.FileHubUid : undefined,
        url: url!,
        size: size!,
        type: finalType,
      });

      showToast(
        isEditMode ? "File updated successfully" : "File uploaded successfully",
        "success",
      );
      onSuccess?.();
      handleClose();
    } catch (error: any) {
      console.error("Save failed:", error);
      showToast(error?.response?.data || "Failed to save file.", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelectedFileType("");
    setSelectedFile(null);
    setFileError("");
    setTypeError("");
    setEditingFileInfo(null);
    onClose();
  };

  const handleUploadClick = () => {
    document.getElementById("file-upload-input")?.click();
  };

  const shouldShowInfoBox = open && (isEditMode || !!selectedFile);

  const displayName = isEditMode
    ? (selectedFile?.name ?? editingFileInfo?.name ?? "")
    : (selectedFile?.name ?? "");

  const displaySize = isEditMode
    ? selectedFile
      ? formatBytes(selectedFile.size)
      : (editingFileInfo?.size ?? "")
    : selectedFile
      ? formatBytes(selectedFile.size)
      : "";

  return (
    <Modal
      open={open}
      onClose={() => {
        if (isSubmitting) return; // don't enable close while submiting
        handleClose();
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 640,
          bgcolor: "#FFFFFF",
          borderRadius: "12px",
          boxShadow: 24,
          p: 0,
          outline: "none",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            p: 3,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography fontWeight={600} color="#101828" fontSize={18}>
              {isEditMode ? "Edit file" : "Add new file"}
            </Typography>
            <IconButton
              sx={{
                border: "none",
              }}
              onClick={handleClose}
              disabled={isSubmitting}
              size="medium"
            >
              <CloseIcon sx={{ color: "#98A2B3" }} />
            </IconButton>
          </Box>
          <Typography variant="body2" color="#475467">
            {isEditMode
              ? "Update the file details and adjust its type in the File Hub."
              : "Upload a file and select its type to add it to the File Hub."}
          </Typography>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Body */}
        {/* File Upload Area */}
        {shouldShowInfoBox && (
          <Box
            sx={{
              border: "1px solid #D1D5DB",
              borderRadius: "12px",
              p: "16px 24px",
              display: "flex",
              flexDirection: "column",
              gap: "10px",
              m: 3,
            }}
          >
            <InsertDriveFileOutlinedIcon
              sx={{ color: "#98A2B3", width: "20px", height: "20px" }}
            />
            {isEditMode && loadingEditData ? (
              <>
                <Skeleton width="60%" height={22} />
                <Skeleton width="30%" height={18} />
              </>
            ) : (
              <>
                <Typography
                  sx={{ color: "#1F2937", fontWeight: 600, fontSize: "14px" }}
                >
                  <EllipsisTooltip title={displayName}>
                    {displayName}
                  </EllipsisTooltip>
                </Typography>
                <Typography sx={{ color: "#4B5563", fontSize: "14px" }}>
                  {displaySize}
                </Typography>
              </>
            )}
          </Box>
        )}
        <Box
          onClick={handleUploadClick}
          sx={{
            border: "1px solid #D1D5DB",
            borderRadius: "12px",
            p: "16px 24px",
            display: "flex",
            alignItems: "center",
            cursor: "pointer",
            m: 3,
            gap: 2,
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "8px",
              border: "1px solid #EAECF0",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <FileUploadOutlinedIcon sx={{ color: "#344054" }} />
          </Box>
          <Stack gap="4px">
            <Typography
              sx={{
                fontSize: 14,
                color: brand[600],
                fontWeight: 600,
              }}
            >
              Click to upload
            </Typography>
            <Typography color="#475467" sx={{ fontSize: 14 }}>
              All file formats accepted except .EXE (max. 2GB)
            </Typography>
          </Stack>
          <input
            id="file-upload-input"
            type="file"
            style={{ display: "none" }}
            accept="*/*"
            onChange={handleFileChange}
            disabled={isSubmitting}
          />
        </Box>
        {fileError && (
          <Typography sx={{ mx: 3 }} color="error" variant="body2">
            {fileError}
          </Typography>
        )}

        {/* File Type Dropdown */}
        <Stack spacing={1} sx={{ m: 3 }}>
          <Typography variant="subtitle2">
            File type
            <Box component="span" sx={{ color: `var(--error-700)` }}>
              *
            </Box>
          </Typography>
          {isEditMode && loadingEditData ? (
            <Skeleton height={40} sx={{ borderRadius: "12px" }} />
          ) : (
            <Select
              id="add-new-file-type-select"
              value={selectedFileType}
              disabled={loadingEditData || isSubmitting}
              onChange={(e: SelectChangeEvent) => {
                setSelectedFileType(e.target.value as FileHubFileEnum);
                setTypeError("");
              }}
              IconComponent={ExpandMoreIcon}
              displayEmpty
              renderValue={() => selectedFileType || "Select a type"}
              sx={{
                borderRadius: "12px",
                minWidth: "150px",
                backgroundColor: "transparent",
                "& .MuiSelect-select": {
                  display: "flex",
                  alignItems: "center",
                  padding: "8.5px 14px",
                },
              }}
              size="medium"
            >
              <MenuItem value={FileHubFileEnum.CONFIDENTIAL}>
                Confidential
              </MenuItem>
              <MenuItem value={FileHubFileEnum.REGULAR}>Regular</MenuItem>
            </Select>
          )}
          {typeError && (
            <Typography color="error" variant="body2">
              {typeError}
            </Typography>
          )}
        </Stack>

        <Divider sx={{ mb: 2 }} />

        {/* Action Buttons */}
        <Stack
          direction="row"
          spacing={2}
          justifyContent="flex-end"
          sx={{ m: 3 }}
        >
          <Button
            variant="outlined"
            onClick={handleClose}
            disabled={isSubmitting}
            sx={{
              borderRadius: "12px",
              px: 3,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleExchange}
            disabled={!canSubmit}
            sx={{
              px: 2,
              borderRadius: "12px",
              backgroundColor: `${brand[600]} !important`,
              color: `white`,
              "&:hover": {
                bgcolor: `${brand[700]} !important`,
                textDecoration: "underline",
              },
              "&:disabled": {
                backgroundColor: `${gray[300]} !important`,
                color: gray[500],
              },
            }}
          >
            {isSubmitting ? (
              <Stack direction="row" alignItems="center" spacing={1}>
                <CircularProgress
                  size={18}
                  thickness={5}
                  sx={{ color: "white" }}
                />
                <Typography
                  sx={{ color: "white", fontWeight: 600, fontSize: 14 }}
                >
                  {isEditMode ? "Updating..." : "Uploading..."}
                </Typography>
              </Stack>
            ) : isEditMode ? (
              "Update"
            ) : (
              "Upload"
            )}
          </Button>
        </Stack>
      </Box>
    </Modal>
  );
};

export default AddNewFileModal;
